import { useCallback, useEffect, useRef } from "react"
import styled from "@emotion/styled"
import { N100 } from "@teladoc/ui-components/colors"
import { useCommClient } from "./hooks/useCommClient"
import {
  VideocallLayout,
  setRtcClient,
  whenSessionConnected,
  whenSessionEnded,
  getControlButtonWhiteList,
  setControlButtonWhiteList,
  whenSessionConnectFailed,
  whenSessionError,
} from "../ucs"
import { useStore } from "effector-react"
import { useUrlValidation } from "./hooks/useUrlValidation"
import { guestTokenStore } from "./stores/guestToken"
import { usePageNavigation } from "./hooks/usePageNagivation"
import { useGuestJoin, leaveCall } from "./hooks/useGuestJoin"
import { ErrorReason } from "./ErrorReason"
import { FullPageLoader } from "../components/Loader"
import { P800 } from "../assets/colors"
import { getUiProfile } from "./stores/startupConfiguration"
import { useAdminReconnectGracePeriod } from "./hooks/useAdminReconnectGracePeriod"
import { deleteInterpreterAttendee } from "../api"
import { useIFrameEvents } from "../iframe/events"
import type { ConnectCallProperties } from "@teladoc/ucs-client-react"
import { useAppContext } from "../AppContext"
import type { InitialCallState } from "../iframe/events/IFrameEvents"
import type { UrlParams } from "../url/params/UrlParams"
import { logger } from "../logger"

export const VideocallPage = () => {
  const { goToNextPage, goToErrorPage, goToMeetingEnded } =
    usePageNavigation("videocall")
  const { commClientInitParams } = useUrlValidation()
  const { commClient } = useCommClient(commClientInitParams?.environment)
  const guestToken = useStore(guestTokenStore)
  const { joinCallParams, joinError } = useGuestJoin()
  const {
    ui_mode,
    background_replacement_color,
    block_video_quality_survey: disableSurvey,
  } = getUiProfile()
  /*
   * useRef here so we don't have to re-render the "whenSessionEnded" hook and
   * "unwatch" the event for a moment.
   */
  const leftAfterAdminGracePeriodExpired = useRef(false)
  const { iframeEvents } = useIFrameEvents()
  const {
    startCallOnMute,
    startWithVideo,
    initialVolume,
    urlParams,
    iframeEventsInitialCallState,
  } = useInitialCallParams()

  useEffect(() => {
    if (joinError) {
      if (joinError === ErrorReason.CONFERENCE_ENDED) {
        goToMeetingEnded()
      } else {
        goToErrorPage(joinError)
      }
    }
  }, [joinError])

  useEffect(() => {
    const attendeeToken = commClientInitParams?.attendee.attendee_token
    const attendeeId = joinCallParams?.adapterPayload.attendee_id
    const attendanceId = joinCallParams?.adapterPayload.attendance_id
    const conferenceId = joinCallParams?.adapterPayload.conference_id
    if (!attendeeToken || !attendeeId || !attendanceId) return
    const unwatch = whenSessionEnded((sessionEndedArgs) => {
      unwatch() // ensure this is only triggered once
      leaveCall({ attendeeToken, attendeeId, attendanceId })
        .then(() =>
          checkInterpreterCleanup({
            gracePeriodExpired: leftAfterAdminGracePeriodExpired.current,
            attendeeToken,
            attendeeId,
          })
        )
        .then(() =>
          iframeEvents?.onSessionDisconnected?.({
            conferenceId,
            sessionEndedArgs,
          })
        )
        .then(goToNextPage)
        .catch((_e) => goToErrorPage())
    })
    return unwatch
  }, [commClientInitParams, joinCallParams, iframeEvents])

  useEffect(() => {
    const conferenceId = joinCallParams?.adapterPayload.conference_id
    if (!conferenceId) return
    const unwatchSessionConnnectFailed = whenSessionConnectFailed((event) => {
      const message = parseConnectFailedReason(event.error)
      iframeEvents?.onSessionConnectFailed?.({ conferenceId, reason: message })
    })
    const unwatchSessionError = whenSessionError(({ message }) => {
      iframeEvents?.onSessionConnectFailed?.({ conferenceId, reason: message })
    })
    return () => {
      unwatchSessionConnnectFailed()
      unwatchSessionError()
    }
  }, [joinCallParams, iframeEvents])

  useEffect(() => {
    const conferenceId = joinCallParams?.adapterPayload.conference_id
    if (!conferenceId) return
    const unwatch = whenSessionConnected(() => {
      iframeEvents?.onSessionConnected?.({ conferenceId })
    })
    return unwatch
  }, [joinCallParams, iframeEvents])

  const leaveActiveSession = useCallback(() => {
    if (!commClient) return
    for (const activeSession of commClient.sessionManager.activeSessions) {
      activeSession.leave()
    }
  }, [commClient])

  useEffect(() => {
    const onUnload = () => {
      leaveActiveSession()
    }
    return onUnload
  }, [leaveActiveSession])

  const { isAdminGracePeriodExpired } = useAdminReconnectGracePeriod()

  useEffect(() => {
    if (isAdminGracePeriodExpired) {
      logger.info("Leaving session that has no Admin")
      leftAfterAdminGracePeriodExpired.current = true
      leaveActiveSession()
    }
  }, [isAdminGracePeriodExpired, leaveActiveSession])

  const startVideocall = async () => {
    if (!commClient) throw new Error("comm client is not valid")
    if (!guestToken) throw new Error("guest token is not valid")
    if (!joinCallParams) throw new Error("Join call parameters are required")
    iframeEvents?.onSessionConnecting?.({
      conferenceId: joinCallParams.adapterPayload.conference_id,
    })
    const { attendee_token: attendeeToken } =
      commClientInitParams?.attendee || {}
    if (!attendeeToken) throw new Error("attendee token is not valid")

    const { sessionJoinParams, adapterPayload } = joinCallParams

    const customConnectCallProps: Omit<
      ConnectCallProperties,
      "adapterPayload"
    > = {
      // There is a bug in UCS C26.2 and prior where explicitly setting `startWithVideo` to
      // undefined does not correctly apply a default value. This will be fixed in C26.3.
      ...(startCallOnMute !== undefined ? { startCallOnMute } : {}),
      ...(startWithVideo !== undefined ? { startWithVideo } : {}),
      audioVolume: initialVolume,
      ...(background_replacement_color
        ? {
            startWithBackgroundEffect: {
              name: "replacement",
              options: {
                backgroundImage: "color",
                backgroundColor: background_replacement_color,
              },
            },
          }
        : {}),
    }
    logger.info("Starting guest join", {
      customConnectCallProps: JSON.stringify(customConnectCallProps),
      urlParams: JSON.stringify({
        region: urlParams.region,
        hasGuestToken: Boolean(urlParams.guestToken),
        hasParticipantName: Boolean(urlParams.participantName),
        startCallOnMute: urlParams.startCallOnMute,
        startWithVideo: urlParams.startWithVideo,
        initialVolume: urlParams.initialVolume,
      }),
      iframeEventsInitialCallState: JSON.stringify(iframeEventsInitialCallState),
    })
    commClient.sessionManager.startGuestJoin(sessionJoinParams, {
      adapterPayload,
      ...customConnectCallProps,
    })
    setRtcClient(commClient)
  }

  const isReady = Boolean(commClient && guestToken && joinCallParams)

  useEffect(() => {
    if (isReady) startVideocall()
  }, [isReady])

  useEffect(() => {
    if (ui_mode === "no_controls") {
      // Hide all controls including camera and microphone
      setControlButtonWhiteList([])
    } else if (ui_mode === "minimal") {
      // "minimal" mode - show only camera and microphone
      setControlButtonWhiteList(["camera", "microphone"])
    }
  }, [])

  return isReady ? (
    <Container>
      <VideocallLayout
        isBlurBackgroundAllowed={false}
        disableSurvey={disableSurvey}
        isSplitViewEnabled={ui_mode !== "minimal" && ui_mode !== "no_controls"}
      />
    </Container>
  ) : (
    <FullPageLoader size="large" color={P800} />
  )
}

const Container = styled.div`
  background-color: ${N100};
  height: 100%;
  overflow: hidden;
`

const checkInterpreterCleanup = async (props: {
  gracePeriodExpired: boolean
  attendeeToken: string
  attendeeId: number
}) => {
  try {
    if (props.gracePeriodExpired) {
      await deleteInterpreterAttendee(props)
    }
  } catch (e) {
    logger.error("Error cleaning up interpreter attendee", { error: String(e) })
  }
}

export const parseConnectFailedReason = (
  error: unknown
): string | undefined => {
  if (error instanceof Error) {
    if (error.name === "SessionConnectionAbortedError") {
      return "The virtual visit has ended"
    } else {
      return error.message
    }
  }
  return undefined
}

type InitialCallParams = {
  startCallOnMute: boolean | undefined
  startWithVideo: boolean | undefined
  initialVolume: number | undefined
  urlParams: UrlParams
  iframeEventsInitialCallState: InitialCallState | undefined
}

export const useInitialCallParams = (): InitialCallParams => {
  const { iframeEvents } = useIFrameEvents()
  const { urlParams } = useAppContext()
  const iframeEventsInitialCallState = iframeEvents?.getInitialCallState?.()
  // Parent window state events sent before we connected take priority over url params
  return {
    startCallOnMute:
      iframeEventsInitialCallState?.startCallOnMute ??
      urlParams.startCallOnMute,
    startWithVideo:
      iframeEventsInitialCallState?.startWithVideo ?? urlParams.startWithVideo,
    initialVolume:
      iframeEventsInitialCallState?.initialVolume ?? urlParams.initialVolume,
    urlParams,
    iframeEventsInitialCallState,
  }
}
